import React, { lazy, Suspense, useMemo, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close,
  Edit,
  Info,
  Inventory2 as InventoryIcon,
  Link as LinkIcon,
  LocalShipping as LocalShippingIcon,
  Menu as MenuIconMui,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Lead } from '@otw/models/leads/lead';
import { AdminDialog } from 'components/AdminDialog';
import { leadStatusTone, StatusChip } from 'components/StatusChip';
import { TOUCH_MIN } from 'core/theme.tokens';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import { useLeads } from 'hooks/admin/useLeads';
import { VehicleAttachmentProvider } from '../../contexts';
import InventoryTab from './tabs/InventoryTab';
import { getBorderColor, getStatusLabel } from '../../util';

interface LeadDetailDialogProps {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  onEdit: (lead: Lead) => void;
  /** Swimlane title from the board (required for custom columns so the chip is not the raw id). */
  columnName?: string;
  /** Swimlane accent color from the board (custom columns). */
  columnColor?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  const isMobile = useIsMobile();
  if (value !== index) return null;
  return (
    <div role='tabpanel' id={`lead-detail-tabpanel-${index}`} aria-labelledby={`lead-detail-tab-${index}`}>
      <Box sx={{ p: { xs: isMobile ? 1.5 : 2, md: 3 }, overflowX: 'hidden', maxWidth: '100%' }}>{children}</Box>
    </div>
  );
};

interface TabDef {
  label: string;
  icon: React.ReactElement;
}

const TABS: TabDef[] = [
  { label: 'Overview', icon: <Info fontSize='small' /> },
  { label: 'Linked Items', icon: <LinkIcon fontSize='small' /> },
  { label: 'Shipping', icon: <LocalShippingIcon fontSize='small' /> },
  { label: 'Suggested Trucks', icon: <SearchIcon fontSize='small' /> },
  { label: 'Inventory', icon: <InventoryIcon fontSize='small' /> },
];

const OverviewTab = lazy(() => import('./tabs/OverviewTab'));
const LinkedItemsTab = lazy(() => import('./tabs/LinkedItemsTab'));
const ShippingTab = lazy(() => import('./tabs/ShippingTab'));
const SuggestedTrucksTab = lazy(() => import('./tabs/SuggestedTrucksTab'));

const TabFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
);

const getInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const LeadDetailDialog: React.FC<LeadDetailDialogProps> = ({
  open,
  onClose,
  lead: leadProp,
  onEdit,
  columnName,
  columnColor,
}) => {
  const { leads } = useLeads();
  const lead = useMemo(
    () => leads?.find((item) => item.id === leadProp.id) ?? leadProp,
    [leads, leadProp],
  );

  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const touchIconSx = isMobile ? { minWidth: TOUCH_MIN, minHeight: TOUCH_MIN } : undefined;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setMobileMenuAnchor(event.currentTarget);
  const handleMobileMenuClose = () => setMobileMenuAnchor(null);
  const handleMobileMenuSelect = (index: number) => {
    setTabValue(index);
    setMobileMenuAnchor(null);
  };

  if (!lead) return null;

  const accent = columnColor ?? getBorderColor(lead.status, theme);
  const statusChipLabel = columnName ?? getStatusLabel(lead.status);

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: { xs: '100vh', md: '90vh' },
          overflow: 'hidden',
        },
      }}
    >
      <VehicleAttachmentProvider lead={lead}>
        <DialogTitle
          sx={{
            p: { xs: 2, md: 2.5 },
            background: `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.04)} 100%)`,
            borderBottom: `1px solid ${alpha(accent, 0.25)}`,
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: alpha(accent, 0.2),
                color: accent,
                fontWeight: 700,
                width: 44,
                height: 44,
              }}
            >
              {getInitials(lead.customerName || lead.company || '?')}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 0.25 }}>
                <Typography
                  variant='caption'
                  sx={{
                    color: accent,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Lead
                </Typography>
                {lead.leadNumber?.trim() &&
                  (isMobile ? (
                    <StatusChip dense tone='neutral' label={lead.leadNumber.trim()} sx={{ flexShrink: 0 }} />
                  ) : (
                    <Chip
                      size='small'
                      label={lead.leadNumber.trim()}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor: alpha(accent, 0.12),
                        color: accent,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  ))}
                {isMobile ? (
                  <StatusChip
                    dense
                    tone={leadStatusTone(lead.status || '')}
                    label={statusChipLabel}
                    sx={{ flexShrink: 0 }}
                  />
                ) : (
                  <Chip
                    size='small'
                    label={statusChipLabel}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: accent,
                      color: theme.palette.getContrastText(accent),
                      '& .MuiChip-label': { px: 0.85 },
                    }}
                  />
                )}
              </Stack>
              <Typography
                variant='h6'
                fontWeight={800}
                sx={{ lineHeight: 1.15, color: 'text.primary' }}
                noWrap
              >
                {lead.customerName || 'Unknown'}
              </Typography>
              {lead.company && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', lineHeight: 1.3 }}
                  noWrap
                >
                  {lead.company}
                </Typography>
              )}
            </Box>
            <Tooltip title='Edit lead'>
              <IconButton
                onClick={() => onEdit(lead)}
                size='small'
                sx={{
                  color: accent,
                  ...touchIconSx,
                  '&:hover': { bgcolor: alpha(accent, 0.12) },
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={onClose}
              aria-label='Close'
              size='small'
              sx={{
                color: 'text.secondary',
                ...touchIconSx,
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06) },
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Box
          sx={{
            px: { xs: 1.5, md: 3 },
            pt: 1,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {isMobile ? (
            <>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ flexGrow: 1, minWidth: 0 }}>
                {TABS[tabValue]?.icon}
                <Typography variant='subtitle2' fontWeight={700} noWrap>
                  {TABS[tabValue]?.label}
                </Typography>
              </Stack>
              <IconButton
                edge='end'
                size='small'
                aria-label='menu'
                onClick={handleMobileMenuOpen}
                sx={touchIconSx}
              >
                <MenuIconMui />
              </IconButton>
              <Menu anchorEl={mobileMenuAnchor} open={Boolean(mobileMenuAnchor)} onClose={handleMobileMenuClose}>
                {TABS.map((tab, idx) => (
                  <MenuItem
                    key={tab.label}
                    onClick={() => handleMobileMenuSelect(idx)}
                    selected={tabValue === idx}
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      {tab.icon}
                      <Typography variant='body2'>{tab.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                minHeight: 44,
                '& .MuiTab-root': {
                  minHeight: 44,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                },
              }}
            >
              {TABS.map(tab => (
                <Tab key={tab.label} icon={tab.icon} iconPosition='start' label={tab.label} />
              ))}
            </Tabs>
          )}
        </Box>

        <DialogContent dividers sx={{ p: 0, overflowX: 'hidden' }}>
          <TabPanel value={tabValue} index={0}>
            <Suspense fallback={<TabFallback />}>
              <OverviewTab lead={lead} onEdit={onEdit} />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Suspense fallback={<TabFallback />}>
              <LinkedItemsTab
                lead={lead}
                onNavigateToInventoryTab={() => {
                  setTabValue(4);
                  setMobileMenuAnchor(null);
                }}
              />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Suspense fallback={<TabFallback />}>
              <ShippingTab lead={lead} />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Suspense fallback={<TabFallback />}>
              <SuggestedTrucksTab lead={lead} />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Suspense fallback={<TabFallback />}>
              <InventoryTab lead={lead} />
            </Suspense>
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} color='inherit'>
            Close
          </Button>
          <Button onClick={() => onEdit(lead)} variant='contained' startIcon={<Edit />}>
            Edit lead
          </Button>
        </DialogActions>
      </VehicleAttachmentProvider>
    </AdminDialog>
  );
};

export default LeadDetailDialog;
